/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
*/

import React, { useRef } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import { Float, MeshDistortMaterial, Sphere, Environment, Stars } from '@react-three/drei';
import * as THREE from 'three';

const Peppercorn = ({ position, scale = 1, rotationSpeed = 1 }: { position: [number, number, number]; scale?: number, rotationSpeed?: number }) => {
  const ref = useRef<THREE.Mesh>(null);
  
  useFrame((state) => {
    if (ref.current) {
      const t = state.clock.getElapsedTime();
      // Gentle floating rotation
      ref.current.rotation.x = t * 0.2 * rotationSpeed;
      ref.current.rotation.y = t * 0.3 * rotationSpeed;
    }
  });

  return (
    <Sphere ref={ref} args={[1, 32, 32]} position={position} scale={scale}>
      <MeshDistortMaterial
        color="#1a1a1a" // Deep black/brown
        envMapIntensity={1.5}
        clearcoat={0.1}
        clearcoatRoughness={0.8}
        roughness={0.9}
        metalness={0.2}
        distort={0.6} // High distortion for wrinkled surface
        speed={0} // Static distortion shape (wrinkles don't move like liquid)
      />
    </Sphere>
  );
};

export const HeroSpiceScene: React.FC = () => {
  return (
    <div className="absolute inset-0 z-0 opacity-80 pointer-events-none">
      <Canvas camera={{ position: [0, 0, 8], fov: 45 }}>
        <ambientLight intensity={1.5} />
        {/* Warm golden light to simulate sun/spice warmth */}
        <pointLight position={[10, 10, 10]} intensity={2} color="#C5A059" />
        <pointLight position={[-10, -5, -5]} intensity={1} color="#A0522D" />
        
        {/* Main large peppercorn */}
        <Float speed={1.5} rotationIntensity={0.5} floatIntensity={0.5}>
          <Peppercorn position={[0, 0, 0]} scale={2.5} rotationSpeed={0.5} />
        </Float>
        
        {/* Background peppercorns */}
        <Float speed={2} rotationIntensity={1} floatIntensity={1}>
           <Peppercorn position={[-4, 2, -2]} scale={0.8} />
           <Peppercorn position={[4, -2, -3]} scale={1.2} />
           <Peppercorn position={[-3, -3, -5]} scale={0.6} />
           <Peppercorn position={[3, 3, -4]} scale={0.9} />
           <Peppercorn position={[5, 1, -6]} scale={0.7} />
           <Peppercorn position={[-5, 0, -8]} scale={0.5} />
        </Float>

        <Environment preset="sunset" />
      </Canvas>
    </div>
  );
};

export const FloatingPeppercornScene: React.FC = () => {
  return (
    <div className="w-full h-full absolute inset-0">
      <Canvas camera={{ position: [0, 0, 5], fov: 45 }}>
        <ambientLight intensity={1} />
        <spotLight position={[5, 5, 5]} angle={0.5} penumbra={1} intensity={2} color="#ffffff" />
        <Environment preset="studio" />
        
        <Float rotationIntensity={1} floatIntensity={0.5} speed={2}>
           <Peppercorn position={[0, 0, 0]} scale={1.8} />
        </Float>
      </Canvas>
    </div>
  );
}